import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Download, Trash2, Settings, Clock, Eye, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface Profile {
  display_name: string;
  consent_given: boolean;
  consent_date: string;
  privacy_policy_accepted: boolean;
  terms_accepted: boolean;
  data_retention_preference: string;
  created_at: string;
}

interface AuditLog {
  id: string;
  action: string;
  resource_type: string;
  created_at: string;
  details: any;
}

interface PrivacyDashboardProps {
  user: User;
  isOpen?: boolean;
  onClose?: () => void;
  inline?: boolean;
}

export default function PrivacyDashboard({ user, isOpen = true, onClose, inline = false }: PrivacyDashboardProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataRetentionPreference, setDataRetentionPreference] = useState('1year');

  useEffect(() => {
    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen, user]);

  const fetchUserData = async () => {
    try {
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (!profileError && profileData) {
        setProfile(profileData);
        setDataRetentionPreference(profileData.data_retention_preference);
      }

      // Fetch recent audit logs
      const { data: logsData, error: logsError } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!logsError && logsData) {
        setAuditLogs(logsData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Unable to load privacy data');
    } finally {
      setLoading(false);
    }
  };

  const updateDataRetention = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          data_retention_preference: dataRetentionPreference,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Log the privacy setting change
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'privacy_settings_updated',
        p_details: {
          new_retention_preference: dataRetentionPreference,
          timestamp: new Date().toISOString()
        }
      });

      toast.success('Data retention preference updated');
      fetchUserData();
    } catch (error) {
      console.error('Error updating data retention:', error);
      toast.error('Failed to update preferences');
    }
  };

  const exportUserData = async () => {
    try {
      toast.info('Preparing your data export...');
      
      // Fetch ALL user data types (GDPR Article 15 compliance)
      const [
        { data: conversations },
        { data: profileData },
        { data: auditData },
        { data: journalEntries },
        { data: timelineEvents },
        { data: dailyCheckIns },
        { data: whoopRecovery },
        { data: whoopSleep },
        { data: whoopCycle },
        { data: menstrualData },
        { data: wellnessActivities },
        { data: therapyMemories },
        { data: consents },
      ] = await Promise.all([
        supabase.from('therapy_conversations').select('*').eq('user_id', user.id),
        supabase.from('profiles').select('*').eq('user_id', user.id).single(),
        supabase.from('audit_logs').select('*').eq('user_id', user.id),
        (supabase as any).from('journal_entries').select('*').eq('user_id', user.id),
        (supabase as any).from('timeline_events').select('*').eq('user_id', user.id),
        supabase.from('daily_checkins').select('*').eq('user_id', user.id),
        supabase.from('whoop_recovery_data').select('*').eq('user_id', user.id),
        supabase.from('whoop_sleep_data').select('*').eq('user_id', user.id),
        supabase.from('whoop_cycle_data').select('*').eq('user_id', user.id),
        (supabase as any).from('menstrual_cycles').select('*').eq('user_id', user.id),
        supabase.from('wellness_activities').select('*').eq('user_id', user.id),
        supabase.from('therapy_memory_summaries').select('*').eq('user_id', user.id),
        supabase.from('user_consents').select('*').eq('user_id', user.id),
      ]);

      const exportData = {
        user_info: {
          email: user.email,
          user_id: user.id,
          created_at: user.created_at,
          profile: profileData
        },
        therapy_data: {
          conversations: conversations || [],
          memory_summaries: therapyMemories || [],
        },
        wellness_data: {
          daily_checkins: dailyCheckIns || [],
          wellness_activities: wellnessActivities || [],
        },
        personal_data: {
          journal_entries: journalEntries || [],
          timeline_events: timelineEvents || [],
        },
        biometric_data: {
          whoop_recovery: whoopRecovery || [],
          whoop_sleep: whoopSleep || [],
          whoop_cycle: whoopCycle || [],
        },
        reproductive_health: {
          menstrual_data: menstrualData || [],
        },
        compliance: {
          audit_logs: auditData || [],
          consents: consents || [],
        },
        export_metadata: {
          export_date: new Date().toISOString(),
          export_format: 'JSON',
          data_controller: 'Therappi Ltd',
          export_type: 'GDPR Article 15 - Right to Access',
        }
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `therappi-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Log data export for compliance
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'data_exported',
        p_details: {
          conversations_count: conversations?.length || 0,
          timestamp: new Date().toISOString()
        }
      });

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const deleteAllData = async () => {
    if (!confirm('Are you sure you want to delete all your Therappi® data? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete conversations
      const { error: convError } = await supabase
        .from('conversations')
        .delete()
        .eq('user_id', user.id);

      if (convError) throw convError;

      // Log data deletion
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'data_deleted',
        p_details: {
          timestamp: new Date().toISOString(),
          deletion_requested_by_user: true
        }
      });

      toast.success('All Therappi® data has been deleted');
      fetchUserData();
    } catch (error) {
      console.error('Error deleting data:', error);
      toast.error('Failed to delete data');
    }
  };

  if (!isOpen) return null;

  // Inline mode - renders without modal wrapper
  if (inline) {
    return (
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Account Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Account Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Member Since</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(user.created_at || '').toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Consent Status</p>
                    <Badge variant={profile?.consent_given ? "default" : "destructive"}>
                      {profile?.consent_given ? "Given" : "Not Given"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Privacy Policy</p>
                    <Badge variant={profile?.privacy_policy_accepted ? "default" : "destructive"}>
                      {profile?.privacy_policy_accepted ? "Accepted" : "Not Accepted"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Data Retention Preferences
                </CardTitle>
                <CardDescription>
                  Choose how long you want your Therappi<sup className="text-[0.6em]">®</sup> data to be retained
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Select value={dataRetentionPreference} onValueChange={setDataRetentionPreference}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1year">1 Year</SelectItem>
                      <SelectItem value="2years">2 Years</SelectItem>
                      <SelectItem value="5years">5 Years</SelectItem>
                      <SelectItem value="indefinite">Indefinite</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={updateDataRetention}>Update Preference</Button>
                </div>
                
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Data will be automatically deleted after the selected period. You can always export your data before deletion.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Data Management Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Data Management
                </CardTitle>
                <CardDescription>
                  Export or delete your Therappi<sup className="text-[0.6em]">®</sup> data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Button onClick={exportUserData} className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export My Data
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={deleteAllData}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete All Data
                  </Button>
                </div>
                
                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Your Rights:</strong> Under GDPR and other privacy laws, you have the right to access, 
                    export, and delete your personal data. All actions are logged for compliance.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>
                  Your recent Therappi<sup className="text-[0.6em]">®</sup> session and privacy-related activities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {auditLogs.length > 0 ? (
                    auditLogs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                        <div>
                          <p className="font-medium text-sm capitalize">
                            {log.action.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant="outline">{log.resource_type}</Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No recent activity to display
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // Modal mode - original behaviour
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-primary" />
              <div>
                <h2 className="text-2xl font-bold">Privacy & Data Management</h2>
                <p className="text-muted-foreground">Manage your data and privacy preferences</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose}>✕</Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Account Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Account Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium">Email</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Member Since</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(user.created_at || '').toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Consent Status</p>
                      <Badge variant={profile?.consent_given ? "default" : "destructive"}>
                        {profile?.consent_given ? "Given" : "Not Given"}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Privacy Policy</p>
                      <Badge variant={profile?.privacy_policy_accepted ? "default" : "destructive"}>
                        {profile?.privacy_policy_accepted ? "Accepted" : "Not Accepted"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Data Retention Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Data Retention Preferences
                  </CardTitle>
                  <CardDescription>
                    Choose how long you want your Therappi<sup className="text-[0.6em]">®</sup> data to be retained
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Select value={dataRetentionPreference} onValueChange={setDataRetentionPreference}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1year">1 Year</SelectItem>
                        <SelectItem value="2years">2 Years</SelectItem>
                        <SelectItem value="5years">5 Years</SelectItem>
                        <SelectItem value="indefinite">Indefinite</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={updateDataRetention}>Update Preference</Button>
                  </div>
                  
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Data will be automatically deleted after the selected period. You can always export your data before deletion.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Data Management Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Data Management
                  </CardTitle>
                  <CardDescription>
                    Export or delete your Therappi<sup className="text-[0.6em]">®</sup> data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4">
                    <Button onClick={exportUserData} className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Export My Data
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={deleteAllData}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete All Data
                    </Button>
                  </div>
                  
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Your Rights:</strong> Under GDPR and other privacy laws, you have the right to access, 
                      export, and delete your personal data. All actions are logged for compliance.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>
                    Your recent Therappi<sup className="text-[0.6em]">®</sup> session and privacy-related activities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {auditLogs.length > 0 ? (
                      auditLogs.map((log) => (
                        <div key={log.id} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                          <div>
                            <p className="font-medium text-sm capitalize">
                              {log.action.replace(/_/g, ' ')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(log.created_at).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="outline">{log.resource_type}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No recent activity to display
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}